import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DefaultButton,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
  mergeStyles,
} from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  getAllProjects,
  getSearchedProjects,
} from "../../modules/redux/Projects/project";
import { ProjectService } from "../../services/ProjectService";
import useIsSignedIn from "../../hooks/useIsSignedIn";
import useModal from "../../hooks/useModal";
import { CreateProjectModal } from "../../components/modals/CreateProjectModal";
import SearchBar from "../../components/SearchBox";
import Loader from "../../components/Loader";
import { colorPallete } from "../../assets/color";
import { formatMoney } from "../../utils";
import { Project } from "../../models";

// DEX-648 — Pipeline dashboard at /dash. Direct-URL only; renders inside Layout
// (header, profile, and the Layout auth gate come from there). Honest mapping to
// the real data model: status is only Active/Closed, metrics come from the
// server-aggregated /projects/statuses, and the only wireable per-row action is
// "Open project" (there is no project-stage pipeline or review/agreement flow).

type PipelineMetrics = { total: number; active: number; closed: number };

const Dash: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isSignedIn = useIsSignedIn();

  const isSearchEnabled = useAppSelector(
    (state) => state.isSearchEnabled.value
  );
  const searchParam = useAppSelector((state) => state.isSearchEnabled.text);
  const { isLoading, data, error } = useAppSelector((state) =>
    isSearchEnabled ? state.fetchSearchedProject : state.fetchProjects
  );

  const [projects, setProjects] = useState<Project[]>([]);
  const [metrics, setMetrics] = useState<PipelineMetrics | null>(null);
  const [metricsError, setMetricsError] = useState(false);
  const [projectLimit, setProjectLimit] = useState<number>(0);
  const { isModalOpen, showModal, hideModal } = useModal();

  // List: reuse the app-wide search-vs-all switch driven by the shared SearchBox.
  useEffect(() => {
    if (!isSearchEnabled) {
      dispatch(getAllProjects({ limit: 500, offset: 0 }));
    }
  }, [isSearchEnabled, dispatch]);

  useEffect(() => {
    if (isSearchEnabled) {
      dispatch(
        getSearchedProjects({ projectName: searchParam, limit: 120, offset: 0 })
      );
    }
  }, [isSearchEnabled, searchParam, dispatch]);

  useEffect(() => {
    if (!isLoading) {
      setProjects(data && data.length > 0 ? data : []);
    }
  }, [isLoading, data]);

  // Pipeline metrics are intentionally independent of the list search — they stay
  // scoped to the whole pipeline (server aggregate), so searching never changes them.
  useEffect(() => {
    if (!isSignedIn) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await ProjectService.getStatuses();
        if (cancelled) return;
        const active: number = res?.data?.activeValue ?? 0;
        const closed: number = res?.data?.closedValue ?? 0;
        setMetrics({ total: active + closed, active, closed });
        setMetricsError(false);
      } catch {
        if (!cancelled) setMetricsError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSignedIn]);

  // Project-creation limit gates the "New project" button (same source the existing
  // New Project tile uses).
  useEffect(() => {
    if (!isSignedIn) return;
    (async () => {
      try {
        const limit: any = await ProjectService.getProjectLimit();
        setProjectLimit(limit?.data?.[0]?.value ?? 0);
      } catch {
        /* non-fatal: creation is still gated by the modal/server */
      }
    })();
  }, [isSignedIn]);

  const openProject = (project: Project) => {
    if (project.id === undefined) return;
    navigate(`/home/${project.id}`, {
      state: { review: false, fileName: null },
    });
  };

  const counterpartiesOf = (project: Project): string =>
    (project.counterParties ?? "")
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", ");

  const renderMetric = (label: string, value: number | undefined) => (
    <Stack className={metricTile} aria-live="polite">
      <Text className={metricLabel}>{label}</Text>
      {metricsError ? (
        <Text className={metricUnavailable}>Unavailable</Text>
      ) : metrics ? (
        <Text className={metricValue}>{formatMoney(value)}</Text>
      ) : (
        <Spinner size={SpinnerSize.small} ariaLabel="Loading metric" />
      )}
    </Stack>
  );

  const renderList = () => {
    if (isLoading) {
      return (
        <Stack className={stateBlock}>
          <Loader />
          <Text className={stateText}>{t("loading")}</Text>
        </Stack>
      );
    }
    if (error === "Network Error") {
      return (
        <Stack className={stateBlock}>
          <Text className={stateText}>{t("loader")}</Text>
          <PrimaryButton
            onClick={() => dispatch(getAllProjects({ limit: 500, offset: 0 }))}
          >
            {t("retry")}
          </PrimaryButton>
        </Stack>
      );
    }
    if (projects.length === 0) {
      return (
        <Stack className={stateBlock}>
          <Text className={stateText}>
            {isSearchEnabled
              ? "No projects match your search."
              : t("no_projects_found")}
          </Text>
        </Stack>
      );
    }
    return (
      <div role="table" aria-label="Projects" className={listSurface}>
        <div role="row" className={`${rowBase} ${headerRow}`}>
          <span role="columnheader" className={colName}>
            Project
          </span>
          <span role="columnheader" className={colStage}>
            Stage
          </span>
          <span role="columnheader" className={colValue}>
            Value
          </span>
          <span role="columnheader" className={colAction}>
            Next action
          </span>
        </div>
        {projects.map((project) => {
          const parties = counterpartiesOf(project);
          const hasValue =
            project.value !== null && project.value !== undefined;
          const stage = project.status || "—";
          const isClosed = stage.toLowerCase() === "closed";
          return (
            <div role="row" key={project.id} className={rowBase}>
              <span role="cell" className={colName}>
                <Text className={projectName}>{project.name}</Text>
                {parties && <Text className={projectParties}>{parties}</Text>}
              </span>
              <span role="cell" className={colStage}>
                <span
                  className={`${stageBadge} ${
                    isClosed ? stageClosed : stageActive
                  }`}
                >
                  {stage}
                </span>
              </span>
              <span role="cell" className={`${colValue} ${valueCell}`}>
                {hasValue ? formatMoney(project.value) : "—"}
              </span>
              <span role="cell" className={colAction}>
                <DefaultButton
                  onClick={() => openProject(project)}
                  ariaLabel={`Open project ${project.name}`}
                  styles={{ root: { minWidth: 120 } }}
                >
                  Open project
                </DefaultButton>
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={page}>
      <div className={container}>
        {/* Page introduction */}
        <div className={introRow}>
          <Stack>
            <Text className={eyebrow}>Your workspace</Text>
            <Text as="h1" className={heading}>
              Keep every deal moving.
            </Text>
            <Text className={subheading}>
              Projects, counterparties, and your next steps.
            </Text>
          </Stack>
          <PrimaryButton
            className={newProjectBtn}
            onClick={showModal}
            disabled={projectLimit === 0}
            title={
              projectLimit === 0
                ? "You have reached your project creation limit."
                : undefined
            }
          >
            New project
          </PrimaryButton>
          <CreateProjectModal
            isModalOpen={isModalOpen}
            showModal={showModal}
            hideModal={hideModal}
            setProjectLimit={setProjectLimit}
          />
        </div>

        {/* Pipeline summary (scoped to the whole pipeline) */}
        <div className={metricsRow}>
          {renderMetric("Total pipeline value", metrics?.total)}
          {renderMetric("Active value", metrics?.active)}
          {renderMetric("Closed value", metrics?.closed)}
        </div>

        {/* Projects */}
        <div className={projectsHeader}>
          <Text className={sectionTitle}>Projects</Text>
          {/* Backend /search/projects matches project name only (DEX-650);
              don't promise counterparty search the API can't honor. */}
          <SearchBar placeholder="Search projects by name" />
        </div>
        {renderList()}
      </div>
    </div>
  );
};

export default Dash;

/* ---- styles (Fluent mergeStyles; mirrors app conventions: Poppins, colorPallete) ---- */

const FONT = "Poppins";

const page = mergeStyles({
  width: "100%",
  backgroundColor: colorPallete.background,
  minHeight: "100%",
});

const container = mergeStyles({
  maxWidth: 1120,
  margin: "0 auto",
  padding: "28px 24px 48px",
  boxSizing: "border-box",
});

const introRow = mergeStyles({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 16,
  flexWrap: "wrap",
});

const eyebrow = mergeStyles({
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: colorPallete.grey,
});

const heading = mergeStyles({
  fontFamily: FONT,
  fontSize: 26,
  fontWeight: 600,
  color: colorPallete.darkGrey,
  margin: "4px 0 2px",
});

const subheading = mergeStyles({
  fontFamily: FONT,
  fontSize: 14,
  color: colorPallete.grey,
});

const newProjectBtn = mergeStyles({
  backgroundColor: colorPallete.btBlue,
  borderColor: colorPallete.btBlue,
  height: 40,
  borderRadius: 6,
  fontFamily: FONT,
});

const metricsRow = mergeStyles({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: 16,
  margin: "22px 0 28px",
  selectors: {
    "@media (max-width: 720px)": { gridTemplateColumns: "1fr" },
  },
});

const metricTile = mergeStyles({
  backgroundColor: colorPallete.white,
  border: `1px solid ${colorPallete.grey3}`,
  borderRadius: 8,
  padding: "16px 18px",
  minHeight: 78,
  justifyContent: "center",
});

const metricLabel = mergeStyles({
  fontFamily: FONT,
  fontSize: 13,
  color: colorPallete.grey,
  marginBottom: 6,
});

const metricValue = mergeStyles({
  fontFamily: FONT,
  fontSize: 24,
  fontWeight: 600,
  color: colorPallete.darkGrey,
  fontVariantNumeric: "tabular-nums",
});

const metricUnavailable = mergeStyles({
  fontFamily: FONT,
  fontSize: 14,
  color: colorPallete.grey1,
});

const projectsHeader = mergeStyles({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  marginBottom: 12,
  flexWrap: "wrap",
});

const sectionTitle = mergeStyles({
  fontFamily: FONT,
  fontSize: 18,
  fontWeight: 600,
  color: colorPallete.darkGrey,
});

const listSurface = mergeStyles({
  backgroundColor: colorPallete.white,
  border: `1px solid ${colorPallete.grey3}`,
  borderRadius: 8,
  overflow: "hidden",
});

const rowBase = mergeStyles({
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 120px 140px 150px",
  alignItems: "center",
  gap: 12,
  padding: "14px 18px",
  borderBottom: `1px solid ${colorPallete.lightestGrey}`,
  selectors: {
    "@media (max-width: 720px)": {
      gridTemplateColumns: "1fr",
      gap: 6,
    },
  },
});

const headerRow = mergeStyles({
  backgroundColor: colorPallete.lightestGrey,
  selectors: {
    "@media (max-width: 720px)": { display: "none" },
  },
});

const colName = mergeStyles({ display: "flex", flexDirection: "column", minWidth: 0 });
const colStage = mergeStyles({});
const colValue = mergeStyles({ textAlign: "right" });
const colAction = mergeStyles({ textAlign: "right" });

const projectName = mergeStyles({
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 600,
  color: colorPallete.darkGrey,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const projectParties = mergeStyles({
  fontFamily: FONT,
  fontSize: 12.5,
  color: colorPallete.grey,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const stageBadge = mergeStyles({
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 500,
  padding: "3px 10px",
  borderRadius: 12,
  display: "inline-block",
});

const stageActive = mergeStyles({
  backgroundColor: colorPallete.btLightBlue,
  color: colorPallete.btBlue,
});

const stageClosed = mergeStyles({
  backgroundColor: colorPallete.lightestGrey,
  color: colorPallete.grey,
});

const valueCell = mergeStyles({
  fontFamily: FONT,
  fontSize: 14,
  color: colorPallete.darkGrey,
  fontVariantNumeric: "tabular-nums",
});

const stateBlock = mergeStyles({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  padding: "56px 0",
  backgroundColor: colorPallete.white,
  border: `1px solid ${colorPallete.grey3}`,
  borderRadius: 8,
});

const stateText = mergeStyles({
  fontFamily: FONT,
  fontSize: 15,
  color: colorPallete.grey,
});
